// Detecção de locale — separada do setup do i18next pra ser testável isolada.
//
// ORDEM de prioridade (a primeira que resolver vence):
//   1. localStorage `iptv:locale`  — escolha MANUAL do usuário (Configurações).
//                                     Sempre vence tudo; persiste entre sessões.
//   2. Locale do SISTEMA da TV      — webOS (Luna settingsservice) / Tizen
//                                     (systeminfo LOCALE). É o idioma que o dono
//                                     configurou na TV. Async + timeout curto.
//   3. navigator.language(s)        — idioma do WebView. Em web puro é o único
//                                     sinal; no empacotado file:// é frágil.
//   4. DEFAULT_LOCALE (pt-BR)       — sem NENHUMA informação. Produto é BR-first.
//
// Fallback de mapeamento (locale bruto → suportado):
//   - navigator/localStorage sem match e sistema AUSENTE → pt-BR (default histórico).
//   - locale de SISTEMA PRESENTE mas não suportado (ex.: fr-FR, de-DE) → en-US.
//     Racional: a TV está claramente num idioma internacional que não falamos;
//     EN é o mais próximo do "neutro" e alinha com a spec (a11y-i18n §9H.2 diz
//     "fallback EN"). NÃO mexemos no DEFAULT_LOCALE global (afeta web/todo mundo);
//     a preferência por EN é LOCAL a este caminho de sistema.
//
// Boot NUNCA pode pendurar esperando a plataforma: leitura de sistema é uma
// Promise com timeout curto (SYSTEM_LOCALE_TIMEOUT_MS) que resolve `null` se a
// API não existe, dá erro ou demora — aí cai no navigator.language.
//
// webOS: o bridge Luna (`window.webOS.service.request`) é injetado pelo WebView
// NATIVO da TV — NÃO precisa de webOSTV.js empacotado. É o mesmo bridge que
// packages/device/src/device-id.ts já usa em produção pra ler o LGUDID.

export const SUPPORTED_LOCALES = ['pt-BR', 'en-US', 'es-419'] as const
export type Locale = (typeof SUPPORTED_LOCALES)[number]
export const DEFAULT_LOCALE: Locale = 'pt-BR'

export const STORAGE_KEY = 'iptv:locale'

// Timeout curto: Luna (webOS) é callback assíncrono e PODE não responder (API
// ausente numa TV velha, permissão negada, serviço travado). 700ms é folgado o
// bastante pra uma chamada IPC local resolver e curto o bastante pra não atrasar
// o 1º paint de forma perceptível — se estourar, seguimos com navigator.language.
export const SYSTEM_LOCALE_TIMEOUT_MS = 700

// Bridge Luna do webOS. NÃO exige webOSTV.js: o WebView nativo do webOS injeta
// `window.webOS.service.request` (ou `window.PalmServiceBridge`) em runtime — é o
// MESMO bridge que packages/device/src/device-id.ts já usa pra ler o device id.
interface WebosLuna {
  service?: {
    request?: (
      uri: string,
      args: {
        method: string
        parameters?: Record<string, unknown>
        onSuccess?: (res: unknown) => void
        onFailure?: (err: unknown) => void
      },
    ) => unknown
  }
}

// ATENÇÃO: `declare global { interface Window }` MERGEIA por todo o programa.
// packages/device/src/platform.ts JÁ declara `Window.webOS` (com `deviceInfo`)
// e `Window.tizen` (com `getCapability`). TS exige que declarações subsequentes
// da MESMA propriedade tenham tipo IDÊNTICO — então aqui declaramos SUPERCONJUNTOS
// que incluem os campos de lá + os nossos. As duas fontes precisam bater; se
// platform.ts mudar o shape, este precisa acompanhar (e vice-versa).
declare global {
  interface Window {
    // Shape IDÊNTICO ao declarado em packages/device/src/platform.ts (os campos
    // compartilhados TÊM que bater byte-a-byte por causa do merge de `declare
    // global`). `deviceInfo` é de lá; `service` (bridge Luna) é usado aqui.
    webOS?: {
      deviceInfo?: (cb: (d: { modelName?: string; sdkVersion?: string }) => void) => void
      service?: {
        request?: (
          uri: string,
          args: {
            method: string
            parameters?: Record<string, unknown>
            onSuccess?: (res: unknown) => void
            onFailure?: (err: unknown) => void
          },
        ) => unknown
      }
    }
    // Fallback do bridge Luna em algumas builds webOS (espelha device-id.ts).
    PalmServiceBridge?: WebosLuna
    // PalmSystem: bridge nativo, presente mesmo sem service.request. Expõe as
    // locales do sistema como JSON string em `locales` / `localeInfo`.
    PalmSystem?: {
      locales?: string
      localeInfo?: string
    }
    tizen?: {
      systeminfo?: {
        getCapability?: (key: string) => string
        getPropertyValue?: (
          property: string,
          onSuccess: (data: { language?: string; [k: string]: unknown }) => void,
          onError?: (err: unknown) => void,
        ) => void
      }
    }
  }
}

/** Bridge Luna (webOS ou PalmServiceBridge), como em device-id.ts. */
function getWebosBridge(): WebosLuna | null {
  try {
    if (typeof window === 'undefined') return null
    return window.webOS ?? window.PalmServiceBridge ?? null
  } catch {
    return null
  }
}

/**
 * Mapeia um locale bruto (BCP-47 tipo `pt-BR`, `en`, ou POSIX tipo `pt_BR.UTF-8`)
 * pra um dos 3 suportados, por PREFIXO de idioma. Retorna `null` se não casar com
 * nenhum — o caller decide o fallback (pt-BR pra ausência de sistema, en-US pra
 * sistema presente porém exótico).
 */
export function mapToSupportedLocale(raw: string | null | undefined): Locale | null {
  if (!raw || typeof raw !== 'string') return null
  // Normaliza POSIX (`pt_BR.UTF-8`) e variações: pega só o prefixo de idioma.
  const lang = raw.trim().toLowerCase().replace(/_/g, '-').split(/[-.@]/)[0]
  if (!lang) return null
  if (lang === 'pt') return 'pt-BR'
  if (lang === 'es') return 'es-419'
  if (lang === 'en') return 'en-US'
  return null
}

/** Escolha manual persistida do usuário (Configurações). Vence tudo. */
export function readStoredLocale(): Locale | null {
  try {
    if (typeof localStorage === 'undefined') return null
    const stored = localStorage.getItem(STORAGE_KEY) as Locale | null
    if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
      return stored as Locale
    }
  } catch {
    // localStorage pode lançar (modo privado, storage desabilitado).
  }
  return null
}

/**
 * Idioma do WebView. Considera `navigator.languages[]` inteiro (não só o [0]) —
 * melhoria barata: se o 1º idioma não é suportado mas o 2º é, ainda acertamos.
 * Cai em `navigator.language` se `languages` não existir.
 */
export function readNavigatorLocale(): Locale | null {
  try {
    if (typeof navigator === 'undefined') return null
    const list: string[] =
      Array.isArray(navigator.languages) && navigator.languages.length > 0
        ? navigator.languages
        : navigator.language
          ? [navigator.language]
          : []
    for (const cand of list) {
      const mapped = mapToSupportedLocale(cand)
      if (mapped) return mapped
    }
  } catch {
    // acesso a navigator pode lançar em ambientes exóticos.
  }
  return null
}

// ===== Leitura do locale do SISTEMA (plataforma), com timeout =====

/**
 * Envelopa uma promise de leitura de plataforma num timeout curto. Resolve
 * `null` se a plataforma demorar/falhar — NUNCA rejeita, nunca pendura o boot.
 */
function withTimeout(p: Promise<string | null>, ms: number): Promise<string | null> {
  return new Promise((resolve) => {
    let done = false
    const settle = (v: string | null): void => {
      if (done) return
      done = true
      resolve(v)
    }
    const timer = setTimeout(() => settle(null), ms)
    p.then(
      (v) => {
        clearTimeout(timer)
        settle(v)
      },
      () => {
        clearTimeout(timer)
        settle(null)
      },
    )
  })
}

/**
 * webOS (LG): lê `localeInfo.locales.UI` do settingsservice.
 *   - Via bridge Luna `window.webOS.service.request` (se webOSTV.js estiver
 *     presente) — caminho preferido, cobre TVs onde PalmSystem não expõe locale.
 *   - Fallback síncrono via `window.PalmSystem.localeInfo`/`.locales` (JSON
 *     string), que existe no WebView do webOS mesmo sem webOSTV.js.
 * Resolve o locale bruto (ex.: `en-US`) ou `null`.
 */
export function readWebOSSystemLocale(): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      if (typeof window === 'undefined') return resolve(null)

      // 1. Bridge Luna (assíncrono). Só tenta se `service.request` existir.
      const bridge = getWebosBridge()
      const req = bridge?.service?.request
      if (typeof req === 'function') {
        let settled = false
        const finish = (v: string | null): void => {
          if (settled) return
          settled = true
          resolve(v)
        }
        try {
          req('luna://com.webos.settingsservice', {
            method: 'getSystemSettings',
            parameters: { keys: ['localeInfo'] },
            onSuccess: (res: unknown) => {
              // Se a Luna respondeu mas sem UI locale, ainda tenta PalmSystem.
              finish(extractWebOSUiLocale(res) ?? readPalmSystemLocale())
            },
            onFailure: () => {
              // Luna falhou — tenta o fallback PalmSystem antes de desistir.
              finish(readPalmSystemLocale())
            },
          })
          return
        } catch {
          // request lançou de forma síncrona — cai no PalmSystem.
          return resolve(readPalmSystemLocale())
        }
      }

      // 2. Sem bridge Luna: fallback PalmSystem (síncrono).
      return resolve(readPalmSystemLocale())
    } catch {
      return resolve(null)
    }
  })
}

/** Extrai `settings.localeInfo.locales.UI` da resposta do settingsservice. */
export function extractWebOSUiLocale(res: unknown): string | null {
  try {
    const r = res as {
      settings?: { localeInfo?: { locales?: { UI?: string } } }
      // Algumas versões retornam localeInfo no topo, sem `settings`.
      localeInfo?: { locales?: { UI?: string } }
    }
    const ui = r?.settings?.localeInfo?.locales?.UI ?? r?.localeInfo?.locales?.UI
    return typeof ui === 'string' && ui ? ui : null
  } catch {
    return null
  }
}

/** Fallback síncrono via PalmSystem (JSON string com `locales.UI`). */
export function readPalmSystemLocale(): string | null {
  try {
    if (typeof window === 'undefined') return null
    const ps = window.PalmSystem
    if (!ps) return null
    const raw = ps.localeInfo ?? ps.locales
    if (typeof raw !== 'string' || !raw) return null
    const parsed = JSON.parse(raw) as { locales?: { UI?: string }; UI?: string }
    const ui = parsed?.locales?.UI ?? parsed?.UI
    return typeof ui === 'string' && ui ? ui : null
  } catch {
    return null
  }
}

/**
 * Tizen (Samsung): `tizen.systeminfo.getPropertyValue('LOCALE', cb)` → callback
 * com `{ language: 'pt_BR.UTF-8', ... }`. Resolve o locale bruto ou `null`.
 */
export function readTizenSystemLocale(): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      if (typeof window === 'undefined') return resolve(null)
      const getProp = window.tizen?.systeminfo?.getPropertyValue
      if (typeof getProp !== 'function') return resolve(null)
      let settled = false
      const finish = (v: string | null): void => {
        if (settled) return
        settled = true
        resolve(v)
      }
      try {
        getProp(
          'LOCALE',
          (data) => {
            const lang = data?.language
            finish(typeof lang === 'string' && lang ? lang : null)
          },
          () => finish(null),
        )
      } catch {
        finish(null)
      }
    } catch {
      resolve(null)
    }
  })
}

/**
 * Locale do SISTEMA da TV, já mapeado pros suportados. Tenta webOS e Tizen em
 * paralelo (só uma existe por device); o vencedor mapeável ganha. SEMPRE resolve
 * dentro de SYSTEM_LOCALE_TIMEOUT_MS — se nada responder/mapear, resolve `null`
 * e o caller cai no navigator.language.
 *
 * Retorna `{ locale, raw }`: `locale` é o suportado (ou null se não mapeou),
 * `raw` é o valor bruto lido (pode existir mesmo com locale=null → sinaliza
 * "sistema presente porém idioma exótico" pro fallback en-US).
 */
export async function readSystemLocale(
  timeoutMs = SYSTEM_LOCALE_TIMEOUT_MS,
): Promise<{ locale: Locale | null; raw: string | null }> {
  if (typeof window === 'undefined') return { locale: null, raw: null }

  const raws = await Promise.all([
    withTimeout(readWebOSSystemLocale(), timeoutMs),
    withTimeout(readTizenSystemLocale(), timeoutMs),
  ])

  // Primeiro raw não-nulo (só uma plataforma existe por device na prática).
  const raw = raws.find((r) => !!r) ?? null
  return { locale: mapToSupportedLocale(raw), raw }
}

/**
 * Resolve o locale inicial completo, aplicando a ordem de prioridade.
 * ASSÍNCRONA por causa da leitura de sistema (Luna/Tizen são callback).
 *
 *   1. localStorage (escolha do usuário) → vence tudo.
 *   2. Locale do sistema da TV:
 *        - mapeável → usa.
 *        - presente mas exótico (raw!=null, não mapeou) → en-US.
 *   3. navigator.language(s) mapeável → usa.
 *   4. DEFAULT_LOCALE (pt-BR).
 */
export async function detectInitialLocale(): Promise<Locale> {
  // 1. Escolha manual do usuário.
  const stored = readStoredLocale()
  if (stored) return stored

  // 2. Locale do sistema (com timeout embutido).
  const sys = await readSystemLocale()
  if (sys.locale) return sys.locale
  const systemPresentButExotic = sys.raw !== null

  // 3. navigator.
  const nav = readNavigatorLocale()
  if (nav) return nav

  // 4. Fallback. Se o SISTEMA disse algo que não falamos, EN é mais neutro que
  //    o default BR. Sem NENHUM sinal de sistema → pt-BR (default histórico).
  return systemPresentButExotic ? 'en-US' : DEFAULT_LOCALE
}
